import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea, Label } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { RefreshCw, Copy, AlertTriangle, GitMerge, FileX, Sparkles, CheckCircle2, XCircle, Eye, Bot } from "lucide-react";
import { formatNum } from "../lib/utils";
import { toast } from "sonner";

const TYPE_META = {
  duplicate: { tone: "warning", icon: Copy, label: "Duplicate", color: "text-amber-400" },
  contradiction: { tone: "danger", icon: GitMerge, label: "Contradiction", color: "text-red-400" },
  gap: { tone: "warning", icon: FileX, label: "Gap", color: "text-blue-400" },
};

const STATUS_COLUMNS = [
  { key: "open", label: "Open", icon: AlertTriangle, tone: "text-red-400" },
  { key: "investigating", label: "Investigating", icon: Eye, tone: "text-amber-400" },
  { key: "resolved", label: "Resolved", icon: CheckCircle2, tone: "text-emerald-400" },
  { key: "dismissed", label: "Dismissed", icon: XCircle, tone: "text-muted-foreground" },
];

export default function Reconciliation() {
  const [data, setData] = useState({ findings: [], by_type: {}, by_status: {}, last_run_at: null });
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(null);
  const [points, setPoints] = useState([]);
  const [resolveNote, setResolveNote] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/reconciliation/findings?year=2024");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post("/reconciliation/run?year=2024");
      toast.success(`Found ${res.total_findings} issues — ${res.by_type.duplicate || 0} duplicate · ${res.by_type.contradiction || 0} contradiction · ${res.by_type.gap || 0} gap`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setRunning(false); }
  };

  const openFinding = async (f) => {
    setSelected(f);
    setPoints([]);
    setResolveNote("");
    if (f.data_point_ids?.length) {
      const { data } = await api.get(`/reconciliation/data-points?ids=${f.data_point_ids.join(",")}`);
      setPoints(data.items);
    }
  };

  const action = async (act) => {
    try {
      await api.post(`/reconciliation/findings/${selected.id}/action`, { action: act, note: resolveNote });
      toast.success(`Marked ${act === "resolve" ? "resolved" : act === "dismiss" ? "dismissed" : "investigating"}`);
      setSelected(null);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const explain = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.get(`/reconciliation/findings/${selected.id}/explain`);
      setSelected({ ...selected, ai_explanation: data.ai_explanation });
    } catch (e) { toast.error("AI explain failed"); }
    setAiBusy(false);
  };

  const findingsByStatus = STATUS_COLUMNS.reduce((acc, c) => {
    acc[c.key] = data.findings.filter((f) => f.status === c.key);
    return acc;
  }, {});

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Module · Data Assurance</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">AI Reconciliation</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Cross-checks data points from Documents, CSVs, AI Mapper, Connectors and Suppliers. Flags <span className="text-amber-400">duplicates</span>, <span className="text-red-400">contradictions</span>, and <span className="text-blue-400">gaps</span>.
          </p>
        </div>
        <div className="flex items-end gap-3">
          {data.last_run_at && (
            <div className="text-xs font-mono text-muted-foreground self-end mb-1">
              Last run: {new Date(data.last_run_at).toLocaleString("en-SG", {dateStyle: "medium", timeStyle: "short"})}
            </div>
          )}
          <Button variant="primary" onClick={run} disabled={running} data-testid="run-recon-btn">
            <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} /> {running ? "Analysing…" : "Run analysis"}
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(TYPE_META).map(([k, m]) => {
          const Mi = m.icon;
          return (
            <div key={k} className="card-flat p-5" data-testid={`recon-tile-${k}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow">{m.label}</div>
                <Mi className={`h-4 w-4 ${m.color}`} />
              </div>
              <div className="font-display font-bold text-3xl">{data.by_type[k] || 0}</div>
            </div>
          );
        })}
        <div className="card-flat p-5" data-testid="recon-tile-total">
          <div className="flex items-center justify-between mb-2">
            <div className="label-eyebrow">Total findings</div>
            <Bot className="h-4 w-4 text-accent" />
          </div>
          <div className="font-display font-bold text-3xl">{data.findings.length}</div>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="recon-kanban">
        {STATUS_COLUMNS.map((col) => (
          <div key={col.key} className="card-flat" data-testid={`recon-col-${col.key}`}>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <col.icon className={`h-4 w-4 ${col.tone}`} />
                <span className="font-display font-semibold">{col.label}</span>
              </div>
              <Badge variant="outline">{findingsByStatus[col.key]?.length || 0}</Badge>
            </div>
            <div className="p-3 space-y-2 min-h-[200px] max-h-[600px] overflow-y-auto scrollbar-thin">
              {(findingsByStatus[col.key] || []).map((f) => {
                const meta = TYPE_META[f.type] || TYPE_META.duplicate;
                const MI = meta.icon;
                return (
                  <button key={f.id} onClick={() => openFinding(f)}
                    className="w-full text-left p-4 bg-surface border border-border rounded-sm hover:bg-surface-hover transition-colors"
                    data-testid="recon-finding-card">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant={meta.tone}><MI className="h-3 w-3 mr-1" /> {meta.label}</Badge>
                      <span className="text-[10px] font-mono text-muted-foreground">{f.severity}</span>
                    </div>
                    <div className="text-sm font-medium leading-tight mb-1">{f.activity_type || "—"}</div>
                    {f.period && <div className="text-xs font-mono text-muted-foreground">{f.period}</div>}
                    {f.spread_pct !== undefined && f.spread_pct !== null && (
                      <div className="mt-2 text-xs font-mono">
                        Spread: <span className={f.spread_pct >= 25 ? "text-red-400 font-bold" : "text-amber-400"}>{f.spread_pct}%</span>
                      </div>
                    )}
                    {f.missing_periods && (
                      <div className="mt-2 text-xs font-mono text-blue-400">
                        {f.missing_periods.length} months missing
                      </div>
                    )}
                  </button>
                );
              })}
              {(findingsByStatus[col.key] || []).length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-6">None</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={TYPE_META[selected.type]?.tone}>{TYPE_META[selected.type]?.label}</Badge>
                  <Badge variant={selected.severity === "high" ? "danger" : "warning"}>{selected.severity} severity</Badge>
                  {selected.period && <Badge variant="outline">{selected.period}</Badge>}
                </div>
                <DialogTitle>{selected.activity_type || "Reconciliation finding"}</DialogTitle>
                <DialogDescription>{selected.description}</DialogDescription>
              </DialogHeader>

              {selected.sources_involved?.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-2">Sources involved</div>
                  <div className="flex gap-1 flex-wrap">
                    {selected.sources_involved.map((s) => <Badge key={s} variant="lime" className="text-[10px]">{s}</Badge>)}
                  </div>
                </div>
              )}

              {points.length > 0 && (
                <div className="card-flat overflow-hidden">
                  <div className="p-3 border-b border-border bg-surface label-eyebrow">Underlying data points</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Channel</TableHead>
                        <TableHead>Source doc</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">tCO2e</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {points.map((p) => (
                        <TableRow key={p.id} data-testid="recon-dp-row">
                          <TableCell><Badge variant="outline" className="text-[10px]">{p._source_channel}</Badge></TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">{p.source_doc}</TableCell>
                          <TableCell className="text-right font-mono">{formatNum(p.value)}</TableCell>
                          <TableCell className="text-xs font-mono">{p.unit}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{formatNum(p.co2e_tonnes, {decimals: 2})}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {selected.missing_periods && (
                <div className="card-flat p-3 bg-surface">
                  <div className="label-eyebrow mb-2">Missing periods</div>
                  <div className="flex flex-wrap gap-1">
                    {selected.missing_periods.map((m) => <Badge key={m} variant="warning" className="text-[10px] font-mono">{m}</Badge>)}
                  </div>
                </div>
              )}

              {/* AI explanation */}
              {selected.ai_explanation ? (
                <div className="card-flat p-3 bg-accent/5 border-accent/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-3.5 w-3.5 text-accent" />
                    <span className="label-eyebrow">AI recommendation</span>
                  </div>
                  <p className="text-sm text-foreground/90 leading-relaxed">{selected.ai_explanation}</p>
                </div>
              ) : (
                <Button variant="outline" onClick={explain} disabled={aiBusy} className="w-full" data-testid="recon-explain-btn">
                  <Sparkles className={`h-4 w-4 ${aiBusy ? "animate-pulse" : ""}`} /> {aiBusy ? "Asking AI…" : "Get AI recommendation"}
                </Button>
              )}

              {/* Resolve actions */}
              {selected.status !== "resolved" && selected.status !== "dismissed" && (
                <>
                  <div>
                    <Label>Resolution note</Label>
                    <Textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder="Describe what was done / why this is OK…" data-testid="recon-resolve-note" />
                  </div>
                  <div className="flex gap-2">
                    {selected.status === "open" && (
                      <Button variant="outline" onClick={() => action("investigating")} className="flex-1" data-testid="recon-investigate-btn">Mark Investigating</Button>
                    )}
                    <Button variant="ghost" onClick={() => action("dismiss")} className="flex-1" data-testid="recon-dismiss-btn">Dismiss</Button>
                    <Button variant="primary" onClick={() => action("resolve")} className="flex-1" data-testid="recon-resolve-btn">Resolve</Button>
                  </div>
                </>
              )}

              {(selected.status === "resolved" || selected.status === "dismissed") && selected.resolution_note && (
                <div className="card-flat p-3 bg-surface">
                  <div className="label-eyebrow mb-1">Resolution note</div>
                  <div className="text-sm">{selected.resolution_note}</div>
                  <div className="text-xs font-mono text-muted-foreground mt-2">— {selected.resolved_by}</div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
